import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { deleteCloudinaryAsset, fetchCloudinarySignature } from '../../util/cloudinary';
import { setCurrentUser } from '../../ducks/user.duck';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

/////////////////////////
// Upload Certificate //
/////////////////////////
export const uploadCertificateThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadCertificate',
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const signature = await fetchCloudinarySignature({ folder: 'neverscrap' });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', signature.apiKey);
      formData.append('timestamp', signature.timestamp);
      formData.append('signature', signature.signature);
      formData.append('folder', signature.folder);

      const isPdf =
        file?.type === 'application/pdf' || file?.name?.toLowerCase().endsWith('.pdf');
      // Upload PDFs as image resources so Cloudinary can render page previews.
      const resourceType = isPdf ? 'image' : 'image';
      const uploadUrl = `https://api.cloudinary.com/v1_1/${signature.cloudName}/${resourceType}/upload`;
      const response = await window.fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) {
        const message = data?.error?.message || 'Cloudinary upload failed.';
        throw new Error(message);
      }

      const uploadedFile = {
        url: data.secure_url,
        publicId: data.public_id,
        resourceType: data.resource_type,
        format:
          data.format ||
          (file?.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : null) ||
          (file?.type === 'application/pdf' ? 'pdf' : null),
        originalFilename: data.original_filename || file?.name || null,
        bytes: data.bytes,
      };

      return { id, file, uploadedFile };
    } catch (e) {
      return rejectWithValue({ id, error: storableError(e) });
    }
  }
);

export const uploadCertificate = actionPayload => dispatch => {
  return dispatch(uploadCertificateThunk(actionPayload));
};

/////////////////////////
// Delete Certificate  //
/////////////////////////
export const deleteCertificateThunk = createAsyncThunk(
  'ProfileSettingsPage/deleteCertificate',
  async ({ publicId, resourceType }, { rejectWithValue }) => {
    try {
      const response = await deleteCloudinaryAsset({ publicId, resourceType });
      return { publicId, response };
    } catch (e) {
      return rejectWithValue({ error: storableError(e) });
    }
  }
);

export const deleteCertificate = actionPayload => dispatch => {
  return dispatch(deleteCertificateThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    certificates: [],
    certificatesInitialized: false,
    uploadCertificateError: null,
    uploadCertificateInProgress: false,
    deleteCertificateError: null,
    deleteCertificateInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
      state.uploadCertificateError = null;
      state.deleteCertificateError = null;
    },
    setCertificates: (state, action) => {
      state.certificates = action.payload || [];
      state.certificatesInitialized = true;
    },
    removeCertificate: (state, action) => {
      const idToRemove = action.payload;
      state.certificates = state.certificates.filter(cert => {
        const uploadedId = cert.uploadedFile?.publicId || cert.uploadedFile?.url;
        return cert.id !== idToRemove && uploadedId !== idToRemove;
      });
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // uploadCertificate cases
      .addCase(uploadCertificateThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.certificates = [...state.certificates, { id, file, uploading: true }];
        state.uploadCertificateInProgress = true;
        state.uploadCertificateError = null;
      })
      .addCase(uploadCertificateThunk.fulfilled, (state, action) => {
        const { id, uploadedFile } = action.payload;
        state.certificates = state.certificates.map(cert =>
          cert.id === id ? { ...cert, uploadedFile, uploading: false, file: null } : cert
        );
        state.uploadCertificateInProgress = state.certificates.some(cert => cert.uploading);
      })
      .addCase(uploadCertificateThunk.rejected, (state, action) => {
        const { id } = action.payload || {};
        state.certificates = id
          ? state.certificates.filter(cert => cert.id !== id)
          : state.certificates;
        state.uploadCertificateInProgress = state.certificates.some(cert => cert.uploading);
        state.uploadCertificateError = action.payload.error;
      })
      // deleteCertificate cases
      .addCase(deleteCertificateThunk.pending, state => {
        state.deleteCertificateInProgress = true;
        state.deleteCertificateError = null;
      })
      .addCase(deleteCertificateThunk.fulfilled, state => {
        state.deleteCertificateInProgress = false;
      })
      .addCase(deleteCertificateThunk.rejected, (state, action) => {
        state.deleteCertificateInProgress = false;
        state.deleteCertificateError = action.payload.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.certificates = [];
        state.certificatesInitialized = false;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.certificates = [];
        state.certificatesInitialized = false;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const { clearUpdatedForm, setCertificates, removeCertificate } =
  profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
